import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Coins, Plus, Minus, X, Lock, History } from "lucide-react";
import { adminApi, formatErr } from "@/admin/api";
import { AdminInput, AdminTextarea } from "@/admin/components/ui";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

const initial = { amount: 1, customer_note: "", admin_note: "", reference: "" };

export default function ManageCreditsModal({ customer, onClose, onUpdated }) {
  const [tab, setTab] = useState("add");
  const [form, setForm] = useState(initial);
  const [tx, setTx] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [balance, setBalance] = useState(customer.credit_balance ?? 0);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/customers/${customer.id}/credits`);
      setTx(r.data);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadHistory(); /* eslint-disable-line */ }, [customer.id]);

  const submit = async (type) => {
    if (!form.amount || Number(form.amount) < 1) { toast.error("Enter a positive amount."); return; }
    setSaving(true);
    try {
      const r = await adminApi.post("/admin/credits", {
        customer_id: customer.id,
        type,
        amount: Number(form.amount),
        customer_note: form.customer_note,
        admin_note: form.admin_note,
        reference: form.reference,
      });
      setBalance(r.data.balance_after);
      setForm(initial);
      toast.success(type === "add" ? "Credits added." : "Credits removed.");
      onUpdated?.();
      loadHistory();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50" onClick={onClose} data-testid="manage-credits-overlay" />
      <div data-testid="manage-credits-modal" className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto pointer-events-none">
        <div className="glass-strong chrome-border rounded-lg w-full max-w-2xl pointer-events-auto my-auto" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center gap-3 px-5 sm:px-6 py-4 border-b border-white/5">
            <div className="w-10 h-10 rounded-full chrome-border bg-black/40 flex items-center justify-center">
              <Coins className="w-4 h-4 text-[#ff2e93]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] tracking-luxe uppercase text-white/45">Manage Credits</p>
              <p className="font-display text-lg text-chrome truncate">{customer.name} {customer.surname}</p>
              <p className="text-[11px] text-white/55 truncate">{customer.email}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[10px] tracking-luxe uppercase text-white/45">Balance</p>
              <p data-testid="manage-credits-balance" className="font-display text-2xl text-magcyan">{balance}</p>
            </div>
            <button onClick={onClose} data-testid="manage-credits-close" className="ml-2 w-8 h-8 rounded-full border border-white/10 text-white/70 hover:text-white flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-white/5">
            {[
              { id: "add",     label: "Add",     icon: Plus },
              { id: "remove",  label: "Remove",  icon: Minus },
              { id: "history", label: "History", icon: History },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`manage-credits-tab-${t.id}`}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-[10px] tracking-luxe uppercase transition-colors ${tab === t.id ? "text-white border-b-2 border-[#ff2e93]" : "text-white/45 hover:text-white"}`}
              >
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="px-5 sm:px-6 py-5 max-h-[60vh] overflow-y-auto">
            {tab === "add" || tab === "remove" ? (
              <div className="space-y-4" data-testid={`manage-credits-form-${tab}`}>
                <AdminInput data-testid="manage-credits-amount" label="Credit Amount" type="number" min={1} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                {tab === "add" && (
                  <AdminInput data-testid="manage-credits-reference" label="Payment Reference (optional)" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="EFT ref / payment ID" />
                )}
                <AdminTextarea data-testid="manage-credits-customer-note" label="Customer-Visible Note" rows={2} value={form.customer_note} onChange={(e) => setForm({ ...form, customer_note: e.target.value })} placeholder="Shown in the customer's credit history" />
                <AdminTextarea data-testid="manage-credits-admin-note" label={<span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3" /> Admin-Only Note</span>} rows={2} value={form.admin_note} onChange={(e) => setForm({ ...form, admin_note: e.target.value })} placeholder="Never shown to customers" />
                <button
                  onClick={() => submit(tab)}
                  disabled={saving}
                  data-testid={`manage-credits-submit-${tab}`}
                  className={`${tab === "add" ? "btn-primary" : "btn-ghost"} w-full !py-2.5 !text-[11px] disabled:opacity-60`}
                >
                  {tab === "add" ? <><Plus className="w-3.5 h-3.5" /> Add Credits</> : <><Minus className="w-3.5 h-3.5" /> Remove Credits</>}
                </button>
              </div>
            ) : (
              <div data-testid="manage-credits-history">
                {loading ? <p className="text-sm text-white/40">Loading…</p> :
                 tx.length === 0 ? <p className="text-sm text-white/55">No credit transactions yet.</p> : (
                  <div className="divide-y divide-white/5">
                    {tx.map((t) => (
                      <div key={t.id} className="py-3 flex items-start justify-between gap-3" data-testid={`manage-credits-row-${t.id}`}>
                        <div className="min-w-0">
                          <p className="text-[10px] tracking-luxe uppercase text-white/45">{fmtDate(t.created_at)}</p>
                          {(t.customer_note || t.note) && <p className="text-xs text-white/70 mt-0.5">{t.customer_note || t.note}</p>}
                          {t.admin_note && <p className="text-xs text-[#ffd479]/85 mt-0.5"><Lock className="w-3 h-3 inline mr-1" /> {t.admin_note}</p>}
                          {t.reference && <p className="text-[10px] text-white/35 mt-0.5">Ref · {t.reference}</p>}
                        </div>
                        <div className="text-right shrink-0">
                          <p className={`font-display text-lg ${t.type === "add" ? "text-[#7fe6ff]" : "text-[#ff9fcf]"}`}>{t.type === "add" ? "+" : "−"}{t.amount}</p>
                          <p className="text-[10px] text-white/40">{t.balance_before ?? 0} → {t.balance_after ?? 0}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                 )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
